import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  Network,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  LogOut,
  Code,
} from 'lucide-react';
import { useAppStore } from '@/stores/app';
import { useAuth } from '@/contexts/AuthContext';
import { useGardenBadgeStore } from '@/stores/gardenBadge';
import { useClientStore } from '@/stores/client';
import {
  navigation,
  navBuckets,
  pinnedSections,
  groupBySection,
  matchNavItem,
  resolveNavTo,
} from './nav';

// ── Pre-compute section maps (stable across renders) ─────────────────────────

/** All nav items grouped by section. */
const allMainSections = groupBySection(navigation);

/** Pinned sections rendered flat at the top. */
const pinnedNavSections = allMainSections.filter((s) =>
  pinnedSections.has(s.title),
);

/** Lookup: section name → items (for bucket rendering). */
const sectionMap = Object.fromEntries(
  allMainSections.map((s) => [s.title, s.items]),
);

// ─────────────────────────────────────────────────────────────────────────────

export function Sidebar() {
  const collapsed = useAppStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useAppStore((s) => s.toggleSidebar);
  const { pathname } = useLocation();
  const activeTo = matchNavItem(pathname)?.to;
  const { user, signOut, isDevMode } = useAuth();
  const pendingReview = useGardenBadgeStore((s) => s.pendingReview);
  const startPolling = useGardenBadgeStore((s) => s.startPolling);
  const selectedClientUid = useClientStore((s) => s.selectedClientUid);

  // Open state for each bucket (keyed by bucket label)
  const [openBuckets, setOpenBuckets] = useState<Record<string, boolean>>({});

  // Auto-open the bucket containing the active route
  const activeSectionName = useMemo(() => {
    if (!activeTo) return null;
    return navigation.find((i) => i.to === activeTo)?.section ?? null;
  }, [activeTo]);

  useEffect(() => {
    if (!activeSectionName) return;

    const bucket = navBuckets.find((b) =>
      b.sections.includes(activeSectionName),
    );
    if (bucket) {
      setOpenBuckets((prev) =>
        prev[bucket.label] ? prev : { ...prev, [bucket.label]: true },
      );
    }
  }, [activeSectionName]);

  useEffect(() => {
    if (!selectedClientUid) return;
    return startPolling();
  }, [selectedClientUid, startPolling]);

  const userInitials = user?.email
    ? user.email.substring(0, 2).toUpperCase()
    : 'U';

  function toggleBucket(label: string) {
    setOpenBuckets((prev) => ({ ...prev, [label]: !prev[label] }));
  }

  // ── Render helpers ────────────────────────────────────────────────────────

  function renderNavItem(item: (typeof navigation)[number]) {
    const resolvedTo = resolveNavTo(item.to, selectedClientUid);
    return (
      <li key={item.to}>
        <Link
          to={resolvedTo}
          aria-current={activeTo === item.to ? 'page' : undefined}
          className={clsx(
            'flex items-center gap-3 px-3 py-2 rounded-lg transition-colors group relative',
            activeTo === item.to
              ? 'bg-navy-800 text-white border border-navy-700 shadow-sm'
              : 'hover:bg-navy-900 hover:text-white',
            collapsed && 'justify-center px-0',
          )}
        >
          {activeTo === item.to && (
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-brand-teal rounded-r" />
          )}
          <item.icon
            size={18}
            className={clsx(
              'flex-shrink-0 transition-colors',
              activeTo === item.to
                ? 'text-brand-teal'
                : 'group-hover:text-brand-teal',
            )}
          />
          {!collapsed && (
            <span className="font-medium text-sm truncate">{item.label}</span>
          )}
          {!collapsed && item.badge && (
            <span className="ml-auto text-[8px] font-mono bg-brand-teal/10 text-brand-teal px-1.5 py-0.5 rounded-full border border-brand-teal/20">
              {item.badge}
            </span>
          )}
          {!collapsed &&
            item.badgeKey === 'pending_review' &&
            pendingReview > 0 && (
              <span className="ml-auto text-[8px] font-mono bg-purple-500/15 text-purple-300 px-1.5 py-0.5 rounded-full border border-purple-400/30">
                {pendingReview}
              </span>
            )}
        </Link>
      </li>
    );
  }

  function renderSection(title: string, items: (typeof navigation)) {
    return (
      <div key={title}>
        {!collapsed && (
          <h3 className="px-3 mb-2 text-[10px] font-mono uppercase text-slate-600 font-bold tracking-wider">
            {title}
          </h3>
        )}
        <ul className="space-y-1">{items.map(renderNavItem)}</ul>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <aside
      className={clsx(
        'flex flex-col h-full bg-navy-950 text-slate-400 border-r border-navy-800 flex-shrink-0 transition-all duration-200 z-30',
        collapsed ? 'w-16' : 'w-64',
      )}
    >
      {/* Logo */}
      <div className="h-16 flex items-center px-4 border-b border-navy-800 bg-navy-900">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-brand-teal to-brand-green flex items-center justify-center text-navy-900 shadow-glow flex-shrink-0">
            <Network size={16} />
          </div>
          {!collapsed && (
            <span className="text-white font-bold text-lg tracking-tight truncate">
              NeuroGraph
            </span>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-1">

        {/* ── Pinned sections (always flat) ── */}
        <div className="space-y-6 pb-4">
          {pinnedNavSections.map((s) => renderSection(s.title, s.items))}
        </div>

        {/* ── Divider ── */}
        {!collapsed && (
          <div className="mx-3 border-t border-navy-800" />
        )}

        {/* ── Bucketed sections ── */}
        <div className="pt-3 space-y-1">
          {navBuckets.map((bucket) => {
            const isOpen = !!openBuckets[bucket.label];
            const bucketSections = bucket.sections
              .map((name) => ({ title: name, items: sectionMap[name] ?? [] }))
              .filter((s) => s.items.length > 0);

            // Is any item in this bucket currently active?
            const hasActive = bucketSections.some((s) =>
              s.items.some((i) => i.to === activeTo),
            );

            return (
              <div key={bucket.label}>
                <button
                  onClick={() => toggleBucket(bucket.label)}
                  title={collapsed ? bucket.label : undefined}
                  className={clsx(
                    'w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors group',
                    isOpen || hasActive
                      ? 'text-white bg-navy-900'
                      : 'text-slate-400 hover:bg-navy-900 hover:text-white',
                    collapsed && 'justify-center px-0',
                  )}
                >
                  <bucket.icon
                    size={18}
                    className={clsx(
                      'flex-shrink-0 transition-colors',
                      isOpen || hasActive
                        ? 'text-brand-teal'
                        : 'group-hover:text-brand-teal',
                    )}
                  />
                  {!collapsed && (
                    <>
                      <span className="font-semibold text-sm truncate flex-1 text-left">
                        {bucket.label}
                      </span>
                      {isOpen
                        ? <ChevronUp size={14} className="flex-shrink-0 text-slate-500" />
                        : <ChevronDown size={14} className="flex-shrink-0 text-slate-500" />}
                    </>
                  )}
                </button>

                {/* Expanded bucket contents */}
                {isOpen && (
                  <div
                    className={clsx(
                      'mt-1 mb-2 space-y-4',
                      !collapsed && 'border-l-2 border-navy-800 ml-4 pl-2',
                    )}
                  >
                    {bucketSections.map((s) => renderSection(s.title, s.items))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </nav>

      {/* Collapse toggle */}
      <div className="px-2 py-3 border-t border-navy-800">
        <button
          onClick={toggleSidebar}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg hover:bg-navy-900 hover:text-white transition-colors text-sm"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          {!collapsed && <span className="text-xs">Collapse</span>}
        </button>
      </div>

      {/* User section */}
      <div className="p-3 border-t border-navy-800 bg-navy-900">
        <div
          className={clsx(
            'flex items-center gap-3 p-2 rounded-lg',
            collapsed && 'justify-center',
          )}
        >
          <div className="relative flex-shrink-0">
            <div className="w-8 h-8 rounded-full bg-navy-700 flex items-center justify-center text-xs text-brand-teal font-bold border border-navy-600">
              {userInitials}
            </div>
            <span className="absolute bottom-0 right-0 w-2 h-2 bg-brand-green border-2 border-navy-900 rounded-full" />
          </div>
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-white truncate">
                {user?.email || 'User'}
              </p>
              <div className="flex items-center gap-1.5">
                <p className="text-[10px] text-slate-500 truncate">Admin</p>
                {isDevMode && (
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[8px] font-bold bg-amber-500/20 text-amber-400 border border-amber-500/30">
                    <Code size={8} />
                    DEV
                  </span>
                )}
              </div>
            </div>
          )}
          {!collapsed && (
            <button
              onClick={signOut}
              className="p-1.5 rounded-lg hover:bg-navy-700 text-slate-500 hover:text-white transition-colors"
              title="Sign out"
            >
              <LogOut size={14} />
            </button>
          )}
        </div>
        {collapsed && (
          <button
            onClick={signOut}
            className="w-full mt-2 p-2 rounded-lg hover:bg-navy-700 text-slate-500 hover:text-white transition-colors flex items-center justify-center"
            title="Sign out"
          >
            <LogOut size={14} />
          </button>
        )}
      </div>
    </aside>
  );
}
